import { useState, useEffect, useCallback } from 'react';
import TickerBar from './TickerBar';
import ServiceHealthModal from './ServiceHealthModal';
import ServiceHealthAllModal, { getHiddenVendorProducts, isItemVisible } from './ServiceHealthAllModal';
import { TickerItem, ServiceHealthResponse } from '../services/serviceHealthTypes';
import { getTickerSpeed, TICKER_SPEED_DEFAULT } from '../services/userPreferences';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const API_ENDPOINT = '/api/proxy/servicehealth-ticker';
const MAX_TICKER_ITEMS = 8; // Limit ticker to top 8 items for speed

interface ServiceHealthTickerProps {
  showAllModal?: boolean;
  onAllModalClose?: () => void;
}

export default function ServiceHealthTicker({ showAllModal: externalShowAll, onAllModalClose }: ServiceHealthTickerProps = {}) {
  const [items, setItems] = useState<TickerItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<TickerItem | null>(null);
  const [showAllModal, setShowAllModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenProducts, setHiddenProducts] = useState<Set<string>>(new Set());
  const [tickerSpeed, setTickerSpeed] = useState(TICKER_SPEED_DEFAULT);

  const fetchServiceHealth = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINT);

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
      }

      const data: ServiceHealthResponse = await response.json();
      setItems(data.items);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch service health:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceHealth();
    const interval = setInterval(fetchServiceHealth, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchServiceHealth]);

  // Load user preferences on mount
  const loadPreferences = useCallback(async () => {
    try {
      const [hidden, speed] = await Promise.all([
        getHiddenVendorProducts(),
        getTickerSpeed(),
      ]);
      setHiddenProducts(hidden);
      setTickerSpeed(speed);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Sync external modal trigger
  useEffect(() => {
    if (externalShowAll) {
      setShowAllModal(true);
    }
  }, [externalShowAll]);

  const handleItemClick = (item: TickerItem) => {
    setSelectedItem(item);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
  };

  const handleCloseAllModal = () => {
    setShowAllModal(false);
    onAllModalClose?.();
  };

  // Filter out hidden vendor:products and limit for ticker
  const visibleItems = items.filter(item => isItemVisible(item, hiddenProducts));
  const tickerItems = visibleItems.slice(0, MAX_TICKER_ITEMS);
  const totalCount = visibleItems.length;

  if (isLoading && items.length === 0) {
    return (
      <div className="service-health-ticker">
        <div className="ticker-bar ticker-bar-loading">
          <div className="ticker-content-static">
            <span className="ticker-loading">Loading vendor status...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="service-health-ticker">
        <div className="ticker-bar ticker-bar-error">
          <div className="ticker-content-static">
            <span className="ticker-error">Unable to load vendor status</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-health-ticker">
      <TickerBar
        items={tickerItems}
        totalCount={totalCount}
        onItemClick={handleItemClick}
        speed={tickerSpeed}
      />
      {selectedItem && (
        <ServiceHealthModal item={selectedItem} onClose={handleCloseModal} />
      )}
      {showAllModal && (
        <ServiceHealthAllModal
          items={items}
          onItemClick={(item) => {
            setShowAllModal(false);
            onAllModalClose?.();
            setSelectedItem(item);
          }}
          onClose={() => {
            handleCloseAllModal();
            // Reload preferences to pick up any changes
            loadPreferences();
          }}
        />
      )}
    </div>
  );
}
